import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import {
  noticeHasVideoProcessing,
  type VideoAttachmentLike,
} from "../utils/videoAttachment";

const POLL_INTERVAL_MS = 12_000;

/**
 * Refetch notices while any listed notice still has a video awaiting transcode.
 */
export function usePollNoticesWhileVideoProcessing(
  notices: Array<{ attachments?: VideoAttachmentLike[] | null }> | undefined,
  enabled = true
): void {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const shouldPoll = useMemo(() => {
    if (!enabled || !notices?.length) return false;
    return notices.some((n) => noticeHasVideoProcessing(n.attachments));
  }, [enabled, notices]);

  useEffect(() => {
    if (!shouldPoll || !user?.schoolId) return;

    const tick = () => {
      queryClient.invalidateQueries({
        queryKey: ["notices", user.schoolId],
      });
    };

    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [shouldPoll, user?.schoolId, queryClient]);
}
